'use client';

import Link from 'next/link';
import { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();

  return (
    <header className="bg-white dark:bg-[#1e293b] shadow-sm sticky top-0 z-50 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link href="/" className="flex items-center">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1e3a5f] to-[#152a45] dark:from-[#d4a574] dark:to-[#e8c9a3] rounded-lg flex items-center justify-center">
                <span className="text-[#d4a574] dark:text-[#1e3a5f] text-xl">🏢</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#1e3a5f] dark:text-white">GCC Business Hub</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">Trusted Service Providers</p>
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link href="/#categories" className="text-gray-700 dark:text-gray-300 hover:text-[#1e3a5f] dark:hover:text-[#d4a574] font-medium transition">
              {t('nav.categories')}
            </Link>
            <Link href="/#countries" className="text-gray-700 dark:text-gray-300 hover:text-[#1e3a5f] dark:hover:text-[#d4a574] font-medium transition">
              {t('nav.countries')}
            </Link>
            <Link href="/about" className="text-gray-700 dark:text-gray-300 hover:text-[#1e3a5f] dark:hover:text-[#d4a574] font-medium transition">
              {t('nav.about')}
            </Link>
            <Link href="/pricing" className="text-gray-700 dark:text-gray-300 hover:text-[#1e3a5f] dark:hover:text-[#d4a574] font-medium transition">
              {t('nav.pricing')}
            </Link>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </button>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all text-sm"
              aria-label="Toggle language"
            >
              {language === 'en' ? 'AR' : 'EN'}
            </button>

            <Link href="/pricing#contact" className="bg-[#d4a574] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#e8c9a3] transition">
              {t('nav.listBusiness')}
            </Link>
          </nav>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-[#1e3a5f] dark:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-3">
            <Link href="/#categories" className="block text-gray-700 dark:text-gray-300 hover:text-[#1e3a5f] dark:hover:text-[#d4a574] font-medium">
              {t('nav.categories')}
            </Link>
            <Link href="/#countries" className="block text-gray-700 dark:text-gray-300 hover:text-[#1e3a5f] dark:hover:text-[#d4a574] font-medium">
              {t('nav.countries')}
            </Link>
            <Link href="/about" className="block text-gray-700 dark:text-gray-300 hover:text-[#1e3a5f] dark:hover:text-[#d4a574] font-medium">
              {t('nav.about')}
            </Link>
            <Link href="/pricing" className="block text-gray-700 dark:text-gray-300 hover:text-[#1e3a5f] dark:hover:text-[#d4a574] font-medium">
              {t('nav.pricing')}
            </Link>

            {/* Mobile Theme & Language Toggle */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={toggleTheme}
                className="flex-1 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
              >
                {theme === 'light' ? '🌙 Dark' : '☀️ Light'}
              </button>
              <button
                onClick={toggleLanguage}
                className="flex-1 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
              >
                {language === 'en' ? 'العربية' : 'English'}
              </button>
            </div>

            <Link href="/pricing#contact" className="block bg-[#d4a574] text-white px-6 py-2 rounded-lg font-semibold text-center">
              {t('nav.listBusiness')}
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
